import React from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Alert, CircularProgress, Stack,
} from "@mui/material";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import { paymentsApi } from "./api";

/**
 * Add Bank Account — kicks off a GoCardless Billing Request Flow.
 *
 * Deliberately does NOT collect institution / transit / account number inside
 * Step Up. We create a Billing Request + Flow on the server, receive the
 * hosted `authorisation_url`, and send the parent there. GoCardless hosts the
 * actual bank-account form, creates the Customer + Customer Bank Account +
 * Mandate, and bounces the parent back to our configured redirect page.
 *
 * The returned authorisation_url opens in a new tab so staff can keep the
 * parent profile open on their side. The dialog shows a "Setup pending" state
 * until the webhook flips mandate_status to `active`.
 */
type Props = {
  open: boolean;
  parentId: string | number;
  onClose: () => void;
  onSuccess: () => void;
};

const AddBankAccountDialog: React.FC<Props> = ({ open, parentId, onClose, onSuccess }) => {
  const [starting, setStarting] = React.useState(false);
  const [authUrl, setAuthUrl] = React.useState<string | null>(null);
  const [resumed, setResumed] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleStart = async () => {
    setError(null);
    setStarting(true);
    try {
      const res = await paymentsApi.startBankMandateSetup(parentId);
      const data = res?.data ?? res;
      if (!data?.authorisation_url) {
        throw new Error("GoCardless did not return a setup URL.");
      }
      setAuthUrl(data.authorisation_url);
      setResumed(Boolean(data.resumed));
      // Open the hosted flow in a new tab so staff can keep the admin page open.
      window.open(data.authorisation_url, "_blank", "noopener,noreferrer");
    } catch (e: any) {
      setError(e?.message || "Could not start bank setup.");
    } finally {
      setStarting(false);
    }
  };

  const handleDone = () => {
    onSuccess();
    setAuthUrl(null);
    setResumed(false);
    setError(null);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <AccountBalanceOutlinedIcon color="primary" />
        Add Bank Account
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Step Up does not collect the parent's bank account details directly.
          GoCardless will host a secure form where the parent enters their
          institution, transit, and account number.
        </Alert>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!authUrl && (
          <Stack spacing={1.5}>
            <Typography variant="body2">
              This creates a Canadian PAD (Pre-Authorized Debit) mandate the
              parent will authorise once. After that, you can collect monthly
              payments against the mandate without re-asking for banking info.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Clicking <strong>Start Setup</strong> opens the GoCardless hosted
              page in a new tab and emails the parent a copy of the link.
            </Typography>
          </Stack>
        )}

        {authUrl && (
          <Stack spacing={1.5}>
            <Alert severity="success">
              {resumed
                ? "Resumed the existing setup — the parent still needs to complete it."
                : "Setup started. Share the link with the parent if the new tab did not open."}
            </Alert>
            <Button
              variant="outlined"
              startIcon={<OpenInNewOutlinedIcon />}
              href={authUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open GoCardless setup
            </Button>
            <Typography variant="caption" sx={{ wordBreak: "break-all" }} color="text.secondary">
              {authUrl}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Once the parent completes the form, the mandate will show as
              <strong> Active</strong> on this page within a minute or two
              (via GoCardless webhook). You can refresh this page or wait for
              it to appear.
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {!authUrl && (
          <Button
            variant="contained"
            onClick={handleStart}
            disabled={starting}
            startIcon={starting ? <CircularProgress size={16} /> : undefined}
          >
            Start Setup
          </Button>
        )}
        {authUrl && (
          <Button variant="contained" onClick={handleDone}>Done</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddBankAccountDialog;
